import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name too long"),
  email: z.string().trim().email("Invalid email").max(255, "Email too long"),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message too long"),
});

export default function ContactSection() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = contactSchema.safeParse({ name, email, message });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: { name, email, message },
      });

      if (error) throw error;

      setSent(true);
      setName('');
      setEmail('');
      setMessage('');
      toast.success(t('contact.successMessage'));
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('contact.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <section className="py-4 bg-muted/30">
        <div className="container mx-auto px-4">
          <Card className="max-w-md mx-auto text-center">
            <CardContent className="pt-4 pb-4">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <h3 className="text-sm font-semibold mb-1">{t('contact.thankYou')}</h3>
              <p className="text-xs text-muted-foreground">{t('contact.thankYouMessage')}</p>
              <Button 
                variant="outline" 
                size="sm"
                className="mt-2 h-7 text-xs"
                onClick={() => setSent(false)}
              >
                {t('contact.sendAnother')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-3">
          <h2 className="text-base md:text-lg font-bold text-foreground mb-1">
            {t('contact.title')}
          </h2>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <Card className="max-w-md mx-auto">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="flex items-center gap-1.5 text-sm">
              <Mail className="w-3.5 h-3.5 text-primary" />
              {t('contact.formTitle')}
            </CardTitle>
            <CardDescription className="text-[10px]">{t('contact.formDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <form onSubmit={handleSubmit} className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="name" className="text-[10px]">{t('contact.name')}</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('contact.namePlaceholder')}
                    required
                    maxLength={100}
                    className="h-7 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-[10px]">{t('contact.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('contact.emailPlaceholder')}
                    required
                    maxLength={255}
                    className="h-7 text-xs"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="message" className="text-[10px]">{t('contact.message')}</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('contact.messagePlaceholder')}
                  rows={2}
                  required
                  maxLength={2000}
                  className="text-xs min-h-[50px]"
                />
              </div>
              <Button type="submit" className="w-full h-7 text-xs" disabled={loading}>
                {loading ? (
                  t('common.loading')
                ) : (
                  <>
                    <Send className="w-3 h-3 mr-1" />
                    {t('contact.send')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
